/**
 * AvatarUploadDialog (Phase 19.24)
 * - File picker → preview → manual square-crop (drag + zoom) → upload.
 * - Used for both user avatars and (with `mode="business-logo"`) tenant logos.
 * - No extra deps — uses a plain canvas + pointer events for the crop UI.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Loader2, Upload, ZoomIn, ZoomOut, Trash2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VIEWPORT = 280;       // square preview viewport (px)
const OUTPUT_SIZE = 512;    // exported PNG size (square)
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

export const AvatarUploadDialog = ({
  open,
  onOpenChange,
  currentUrl = null,
  mode = 'avatar',             // 'avatar' | 'business-logo'
  applyToPortals = false,
  onUploaded,                  // (resp) => void
}) => {
  const { token, user, refreshUser } = useAuth();
  const [imgSrc, setImgSrc] = useState(null);
  const [imgEl, setImgEl] = useState(null);   // HTMLImageElement (loaded)
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cascade, setCascade] = useState(applyToPortals);
  const fileRef = useRef(null);

  useEffect(() => { setCascade(applyToPortals); }, [applyToPortals, open]);

  // Reset state whenever dialog closes
  useEffect(() => {
    if (!open) {
      setImgSrc(null);
      setImgEl(null);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      setUploading(false);
    }
  }, [open]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be 2 MB or smaller');
      return;
    }
    const allowed = ['image/png','image/jpeg','image/jpg','image/webp','image/svg+xml'];
    if (!allowed.includes(file.type)) {
      toast.error('Use PNG / JPG / WEBP / SVG');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImgSrc(reader.result);
      const img = new window.Image();
      img.onload = () => {
        setImgEl(img);
        setZoom(1);
        setOffset({ x: 0, y: 0 });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const onPointerDown = (e) => {
    setDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };
  const onPointerMove = (e) => {
    if (!dragging || !dragStart) return;
    setOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const onPointerUp = () => { setDragging(false); setDragStart(null); };

  /** Draw the cropped square at OUTPUT_SIZE and return a Blob */
  const exportBlob = () => new Promise((resolve, reject) => {
    if (!imgEl) return reject(new Error('No image'));
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

    // Compute the displayed image size in the viewport (base-fit "cover", then zoom)
    const scale = Math.max(VIEWPORT / imgEl.width, VIEWPORT / imgEl.height) * zoom;
    const dispW = imgEl.width * scale;
    const dispH = imgEl.height * scale;
    // Center the image, then apply offset
    const left = (VIEWPORT - dispW) / 2 + offset.x;
    const top = (VIEWPORT - dispH) / 2 + offset.y;
    // Translate viewport coords → canvas coords (uniform scale to OUTPUT_SIZE)
    const k = OUTPUT_SIZE / VIEWPORT;
    ctx.drawImage(imgEl, left * k, top * k, dispW * k, dispH * k);
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('toBlob failed')), 'image/png', 0.92);
  });

  const handleUpload = async () => {
    if (!imgEl) {
      toast.error('Please pick an image first');
      return;
    }
    setUploading(true);
    try {
      const blob = await exportBlob();
      const form = new FormData();
      form.append('file', blob, 'upload.png');
      let endpoint = `${API_URL}/api/uploads/avatar`;
      if (mode === 'business-logo') {
        endpoint = `${API_URL}/api/uploads/business-logo`;
        form.append('apply_to_portals', cascade ? 'true' : 'false');
      }
      const resp = await axios.post(endpoint, form, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(mode === 'business-logo' ? 'Business logo updated' : 'Profile picture updated');
      if (mode === 'avatar' && refreshUser) {
        try { await refreshUser(); } catch (e) { /* ignore */ }
      }
      onUploaded?.(resp.data);
      onOpenChange(false);
    } catch (err) {
      console.error('upload error', err);
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove current picture?')) return;
    setUploading(true);
    try {
      const endpoint = mode === 'business-logo'
        ? `${API_URL}/api/uploads/business-logo`
        : `${API_URL}/api/uploads/avatar`;
      await axios.delete(endpoint, { headers: { 'Authorization': `Bearer ${token}` } });
      toast.success('Removed');
      if (mode === 'avatar' && refreshUser) {
        try { await refreshUser(); } catch (e) { /* ignore */ }
      }
      onUploaded?.({ removed: true });
      onOpenChange(false);
    } catch (err) {
      toast.error('Failed to remove');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="avatar-upload-dialog">
        <DialogHeader>
          <DialogTitle>
            {mode === 'business-logo' ? 'Business logo' : 'Profile picture'}
          </DialogTitle>
          <DialogDescription>
            PNG, JPG, WEBP, or SVG · 2 MB max · square recommended.
            Drag the image and use the zoom slider to position the crop.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current image preview (if exists) */}
          {!imgSrc && currentUrl && (
            <div className="flex items-center gap-3 p-3 border rounded-lg">
              <img
                src={currentUrl.startsWith('http') ? currentUrl : `${API_URL}${currentUrl}?auth=${token}`}
                alt="Current"
                className="w-16 h-16 rounded-full object-cover"
              />
              <div className="text-sm text-muted-foreground">Currently set. Pick a new image below to replace.</div>
            </div>
          )}

          {/* File input */}
          <div>
            <Label className="block mb-2">Choose image</Label>
            <Input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
              onChange={handleFile}
              data-testid="avatar-file-input"
            />
          </div>

          {/* Crop viewport */}
          {imgEl && (
            <div className="space-y-3" data-testid="avatar-crop-area">
              <div
                className="relative mx-auto rounded-full overflow-hidden border-4 border-primary/30 bg-muted cursor-grab active:cursor-grabbing select-none"
                style={{ width: VIEWPORT, height: VIEWPORT }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerUp}
              >
                <img
                  src={imgSrc}
                  alt="Preview"
                  draggable="false"
                  className="absolute"
                  style={{
                    left: `calc(50% + ${offset.x}px)`,
                    top: `calc(50% + ${offset.y}px)`,
                    transform: `translate(-50%, -50%) scale(${zoom})`,
                    minWidth: '100%',
                    minHeight: '100%',
                    objectFit: 'cover',
                    pointerEvents: 'none',
                  }}
                />
              </div>
              <div className="flex items-center gap-3">
                <ZoomOut className="w-4 h-4 text-muted-foreground" />
                <input
                  type="range"
                  min={MIN_ZOOM}
                  max={MAX_ZOOM}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="flex-1 accent-primary"
                  data-testid="avatar-zoom-slider"
                />
                <ZoomIn className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>
          )}

          {/* Business logo cascade toggle */}
          {mode === 'business-logo' && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={cascade}
                onChange={(e) => setCascade(e.target.checked)}
                className="accent-primary"
                data-testid="logo-cascade-toggle"
              />
              <span>Show this logo in Employee &amp; Client portals too</span>
            </label>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {currentUrl && (
            <Button
              type="button"
              variant="outline"
              onClick={handleRemove}
              disabled={uploading}
              className="mr-auto text-destructive border-destructive/30 hover:bg-destructive/10"
              data-testid="avatar-remove-btn"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Remove
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={uploading || !imgEl}
            data-testid="avatar-save-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AvatarUploadDialog;
